import { useState, useEffect, type FC } from 'react';
import { useCustom } from '@refinedev/core';
import { FileEdit, FileText, Globe, PenLine } from 'lucide-react';
import { GuidedTour } from '@/components/onboarding/GuidedTour';
import { usePageTour } from '@/hooks/usePageTour';
import { blogManagerTourSteps } from '@/lib/tour-data';
import StatCard from '@/components/common/StatCard';
import BlogArticleTable, { type BlogArticle } from './components/BlogArticleTable';
import BlogEditorDrawer from './components/BlogEditorDrawer';

interface BlogStats {
  total: number;
  published: number;
  drafts: number;
}

const TOUR_ID = 'admin-blog-manager';

const BlogManagerPage: FC = () => {
  const tour = usePageTour(TOUR_ID);
  useEffect(() => {
    const handler = () => tour.startTour();
    window.addEventListener('restart-page-tour', handler);
    return () => window.removeEventListener('restart-page-tour', handler);
  }, [tour]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<BlogArticle | null>(null);

  const { data } = useCustom<{ articles: BlogArticle[]; total: number }>({
    url: '/api/v1/admin/marketing/blog',
    method: 'get',
    config: { query: { limit: 100 } },
  });

  const stats: BlogStats = {
    total: data?.data?.total ?? 0,
    published: data?.data?.articles?.filter((a) => a.status === 'published').length ?? 0,
    drafts: data?.data?.articles?.filter((a) => a.status === 'draft').length ?? 0,
  };

  return (
    <div className="space-y-6">
      {/* Header gradient */}
      <div
        className="fc-gradient-header rounded-2xl p-6 text-white"
        style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}
      >
        <div className="flex items-center gap-3" data-tour="blog-manager-header">
          <FileEdit className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Blog Manager</h1>
            <p className="mt-0.5 text-xs italic text-blue-200">Gestionnaire de blog</p>
            <p className="mt-1 text-sm text-blue-100">Articles SEO &amp; Content Marketing</p>
          </div>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Total articles" value={stats.total} icon={FileText} iconColor="text-blue-600 dark:text-blue-400" iconBg="bg-blue-50 dark:bg-blue-900/30" />
        <StatCard title="Publiés" value={stats.published} icon={Globe} iconColor="text-emerald-600 dark:text-emerald-400" iconBg="bg-emerald-50 dark:bg-emerald-900/30" />
        <StatCard title="Brouillons" value={stats.drafts} icon={PenLine} iconColor="text-violet-600 dark:text-violet-400" iconBg="bg-violet-50 dark:bg-violet-900/30" />
      </div>

      {/* Tableau */}
      <BlogArticleTable
        onArticleClick={(article) => {
          setSelectedArticle(article);
          setEditorOpen(true);
        }}
        onNewArticle={() => {
          setSelectedArticle(null);
          setEditorOpen(true);
        }}
      />

      {/* Drawer editeur */}
      <BlogEditorDrawer
        open={editorOpen}
        article={selectedArticle}
        onClose={() => {
          setEditorOpen(false);
          setSelectedArticle(null);
        }}
      />
      <GuidedTour tourId={TOUR_ID} steps={blogManagerTourSteps} isActive={tour.isActive} onComplete={tour.completeTour} onSkip={tour.skipTour} />
    </div>
  );
};

export default BlogManagerPage;
